import { useEffect, useRef } from 'react';
import { useAuth as useClerkAuth, useUser as useClerkUser } from '@clerk/react';
import { API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';

/**
 * Bridges a Clerk sign-in into a RiskLo session.
 *
 * Clerk owns sign-in; RiskLo owns authorization, and everything here is keyed on
 * email (Whop, ADMIN_EMAILS, tier lists, user_entities). So when Clerk reports a
 * signed-in user and we have no RiskLo session yet, we hand Clerk's session
 * token to POST /api/auth/clerk, which verifies it server-side and returns the
 * usual `risklo_auth_token`. Every existing gate then works untouched.
 *
 * Renders nothing. Safe to mount unconditionally: with Clerk unconfigured or the
 * user signed out it simply does nothing.
 */
export default function ClerkSessionBridge() {
  const { isLoaded, isSignedIn, getToken, signOut: clerkSignOut } = useClerkAuth();
  const { user: clerkUser } = useClerkUser();
  const { user: riskloUser, handleGoogleSignIn } = useAuth();
  // One exchange per Clerk user — StrictMode double-invokes effects in dev, and
  // a retry loop against a failing exchange would hammer the endpoint.
  const attemptedForRef = useRef(null);
  // Set once an exchange has succeeded. Needed to tell "not signed in yet" apart
  // from "signed out after being signed in" — see the sign-out effect below.
  const exchangedRef = useRef(false);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    // Already have a RiskLo session — nothing to do.
    if (riskloUser?.email) return;

    const clerkId = clerkUser?.id || 'unknown';
    if (attemptedForRef.current === clerkId) return;
    attemptedForRef.current = clerkId;

    let cancelled = false;
    (async () => {
      try {
        const sessionToken = await getToken();
        if (!sessionToken || cancelled) return;
        const res = await fetch(`${API_BASE_URL}/api/auth/clerk`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionToken}`,
          },
          body: JSON.stringify({}),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !data.success || !data.token) {
          // Leave the Clerk session alone but sign out of Clerk so the user is
          // not stuck "signed in" with no RiskLo access and no way to retry.
          console.warn('[Clerk] session exchange failed:', data.error || res.status);
          if (res.status === 401) {
            try { await clerkSignOut(); } catch { /* best effort */ }
          }
          return;
        }
        // Reuse the OAuth-callback branch of handleGoogleSignIn: it persists a
        // server-signed token + user exactly as the Microsoft/Whop flows do.
        exchangedRef.current = true;
        await handleGoogleSignIn({
          token: data.token,
          email: data.user?.email,
          name: data.user?.name,
          picture: data.user?.picture,
          provider: 'clerk',
        });
      } catch (err) {
        if (!cancelled) console.warn('[Clerk] session exchange error:', err?.message || err);
      }
    })();

    return () => { cancelled = true; };
  }, [isLoaded, isSignedIn, riskloUser?.email, clerkUser?.id, getToken, handleGoogleSignIn, clerkSignOut]);

  // Sign-out propagation. AuthContext.signOut() clears only the RiskLo session;
  // Clerk would still report signed-in, so the effect above would immediately
  // re-exchange and the user could never actually log out. When a session we
  // established disappears, end the Clerk session too.
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (!exchangedRef.current) return;      // never had a session — nothing to undo
    if (riskloUser?.email) return;          // still signed in
    exchangedRef.current = false;
    attemptedForRef.current = null;
    (async () => {
      try { await clerkSignOut(); } catch { /* best effort */ }
    })();
  }, [isLoaded, isSignedIn, riskloUser?.email, clerkSignOut]);

  return null;
}
